/**
 * Reminder 24h before the visit. Web Push where the platform really supports it;
 * an .ics file as the extra channel. States are reported honestly — e.g. a Safari
 * tab on iPhone cannot receive push until the app is added to the home screen.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Icon } from '@astryxdesign/core/Icon';
import { HStack, VStack } from '@astryxdesign/core/Layout';
import { Text } from '@astryxdesign/core/Text';
import { Bell, BellSlash, CalendarPlus } from '@phosphor-icons/react';
import type { BookingView } from '@shared/contracts.ts';
import { useTenant } from '@/app/tenant-context.ts';
import { errorText, publicApi } from '@/lib/api.ts';
import { dateTime } from '@/lib/format.ts';
import { bookingIcs, downloadIcs } from '@/lib/ics.ts';
import { currentPushEndpoint, pushSupport, subscribePush } from '@/lib/pwa.ts';

export function ReminderControls({ bookingId, token, compact = false }: { bookingId: string; token: string; compact?: boolean }) {
  const { slug, tenant } = useTenant();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [icsSaved, setIcsSaved] = useState(false);
  const booking = useQuery({ queryKey: ['booking', slug, bookingId], queryFn: ({ signal }) => publicApi.booking(slug, token, signal) });
  const support = pushSupport();
  const b = booking.data;

  if (!b) return null;
  const isActive = b.status === 'confirmed';
  const lessThanDay = new Date(b.starts_at).getTime() - Date.now() < 24 * 3600e3;

  const enable = async () => {
    setError(null);
    try {
      const key = await publicApi.vapidKey();
      if (!key) throw new Error('no-vapid');
      const sub = await subscribePush(slug, key);
      await publicApi.subscribePush(slug, token, sub);
      await qc.invalidateQueries({ queryKey: ['booking', slug, bookingId] });
    } catch (e) {
      const m = (e as Error).message;
      setError(
        m === 'denied'
          ? 'Уведомления запрещены в настройках браузера. Разрешите их для этого сайта или добавьте запись в календарь.'
          : m === 'dismissed'
            ? 'Вы не разрешили уведомления.'
            : m === 'no-vapid' || m === 'no-worker'
              ? 'Уведомления сейчас недоступны. Добавьте запись в календарь.'
              : errorText(e),
      );
    }
  };
  const disable = async () => {
    const endpoint = await currentPushEndpoint(slug);
    if (endpoint) await publicApi.unsubscribePush(slug, token, endpoint);
    await qc.invalidateQueries({ queryKey: ['booking', slug, bookingId] });
  };

  const ics = () => {
    downloadIcs(
      'zapis.ics',
      bookingIcs({
        id: b.id,
        title: `${b.service_name} — ${tenant.name}`,
        startsAt: b.starts_at,
        endsAt: b.ends_at,
        location: tenant.content.address,
        description: `Запись в ${tenant.name}. Телефон: ${tenant.content.phoneDisplay ?? tenant.content.phone}`,
        url: `${location.origin}/s/${slug}/my`,
      }),
    );
    setIcsSaved(true);
  };

  return (
    <VStack gap={2}>
      {!compact && <Text weight="semibold">Напоминание</Text>}
      {isActive && <PushState b={b} support={support.kind} lessThanDay={lessThanDay} preview={tenant.status === 'preview'} onEnable={enable} onDisable={disable} />}
      {error && <Banner status="warning" title={error} collapsible={false} />}
      {isActive && (
        <VStack gap={1}>
          <Button label="Добавить в календарь" variant="secondary" width="100%" icon={<Icon icon={CalendarPlus} />} onClick={ics} />
          {icsSaved && <Text type="supporting">Файл .ics сохранён. Откройте его — напоминание за сутки добавит ваш календарь.</Text>}
        </VStack>
      )}
    </VStack>
  );
}

function PushState({
  b,
  support,
  lessThanDay,
  preview,
  onEnable,
  onDisable,
}: {
  b: BookingView;
  support: ReturnType<typeof pushSupport>['kind'];
  lessThanDay: boolean;
  preview: boolean;
  onEnable: () => Promise<void>;
  onDisable: () => Promise<void>;
}) {
  const { tenant } = useTenant();
  if (b.push_enabled) {
    const job = b.reminder_job;
    const status =
      job?.status === 'sent'
        ? `Напоминание отправлено ${job.sent_at ? dateTime(job.sent_at, tenant.timezone) : ''}`
        : job?.status === 'queued' || job?.status === 'leased'
          ? `Напоминание придёт ${dateTime(job.run_at, tenant.timezone)}`
          : job?.status === 'suppressed_preview'
            ? 'Демо-режим: напоминание не будет отправлено'
            : lessThanDay
              ? 'До записи меньше суток — напоминание не понадобится'
              : 'Уведомления включены';
    return (
      <HStack gap={2} vAlign="center" hAlign="between" wrap="wrap">
        <HStack gap={2} vAlign="center">
          <Icon icon={Bell} color="accent" />
          <Text>{status}</Text>
        </HStack>
        <Button label="Выключить" variant="ghost" size="sm" icon={<Icon icon={BellSlash} />} clickAction={onDisable} />
      </HStack>
    );
  }
  if (lessThanDay) return <Text type="supporting">До записи меньше суток — напоминание за сутки уже не придёт.</Text>;
  if (support === 'ios-needs-install')
    return <Text type="supporting">На iPhone уведомления приходят только из установленного приложения: «Поделиться» → «На экран „Домой“», затем откройте запись оттуда. Или добавьте запись в календарь.</Text>;
  if (support === 'unsupported') return <Text type="supporting">Этот браузер не поддерживает уведомления. Добавьте запись в календарь.</Text>;
  if (support === 'denied') return <Text type="supporting">Уведомления для этого сайта запрещены в настройках браузера.</Text>;
  return (
    <VStack gap={1}>
      <Button label="Напомнить за сутки" variant="primary" width="100%" icon={<Icon icon={Bell} />} clickAction={onEnable} />
      {preview && <Text type="supporting">Демо-режим: подписка сохранится, но уведомления не отправляются.</Text>}
    </VStack>
  );
}
